import type { FilterItemConfig } from '../types';

export interface DependencyGraph {
  /** topo-sorted field names (roots first) */
  order: string[];
  /** name → set of fields that DEPEND on this name (i.e. its direct dependents) */
  dependentsOf: Map<string, Set<string>>;
  /** name → set of fields that this field depends on */
  parentsOf: Map<string, Set<string>>;
}

/**
 * Build a dependency graph from item configs. Topologically sorts via Kahn's algorithm.
 * Throws synchronously on cycle detection.
 *
 * Plan Section "Initialization sequence" — root fields start in parallel; child fields
 * wait until every parent in their dep set reaches READY or ERROR.
 */
export function buildGraph(items: FilterItemConfig[]): DependencyGraph {
  const names = items.map((i) => i.name);
  const nameSet = new Set(names);

  const dependentsOf = new Map<string, Set<string>>();
  const parentsOf = new Map<string, Set<string>>();

  for (const name of names) {
    dependentsOf.set(name, new Set());
    parentsOf.set(name, new Set());
  }

  for (const item of items) {
    const deps = item.dependsOn ?? [];
    for (const dep of deps) {
      if (!nameSet.has(dep.name)) {
        // Dependency points to a field not in the items list — tolerate it
        // (the unknown parent simply has no value to gate on).
        continue;
      }
      parentsOf.get(item.name)!.add(dep.name);
      dependentsOf.get(dep.name)!.add(item.name);
    }
  }

  // Kahn's algorithm
  const inDegree = new Map<string, number>();
  for (const name of names) {
    inDegree.set(name, parentsOf.get(name)!.size);
  }

  const queue: string[] = [];
  for (const [name, deg] of inDegree) {
    if (deg === 0) queue.push(name);
  }

  const order: string[] = [];
  while (queue.length) {
    const next = queue.shift()!;
    order.push(next);
    for (const dep of dependentsOf.get(next) ?? []) {
      const newDeg = (inDegree.get(dep) ?? 0) - 1;
      inDegree.set(dep, newDeg);
      if (newDeg === 0) queue.push(dep);
    }
  }

  if (order.length !== names.length) {
    const unresolved = names.filter((n) => !order.includes(n));
    const message = `[FilterBar] Cyclic dependency detected in \`dependsOn\` declarations. Fields involved (or downstream of cycle): ${unresolved.join(', ')}.`;

    console.error(message);
    throw new Error(message);
  }

  return { order, dependentsOf, parentsOf };
}

/**
 * Direct dependents of `name` — fields whose `dependsOn` includes `name`.
 * Cascade scope walks from here recursively.
 */
export function getDirectDependents(graph: DependencyGraph, name: string): string[] {
  return Array.from(graph.dependentsOf.get(name) ?? []);
}

/**
 * All descendants (transitively) of `name`. Used to scope cascade reload.
 */
export function getDescendants(graph: DependencyGraph, name: string): string[] {
  const seen = new Set<string>();
  const stack = [...(graph.dependentsOf.get(name) ?? [])];
  while (stack.length) {
    const next = stack.pop()!;
    if (seen.has(next)) continue;
    seen.add(next);
    for (const dep of graph.dependentsOf.get(next) ?? []) {
      stack.push(dep);
    }
  }
  return Array.from(seen);
}
